// public/js/features/assets/list-view.js
import { loadTenants } from '../../modules/tenants.js';
import { setupLastPositionHover, disposeLastPositionHovers } from './components/last-position-hover.js';
import { showActionMenu } from './components/asset-action-menu.js';
import { setupBatchImport } from './batch-import-handler.js';
import { formatAssetLabel, getAssetId } from '../../utils/asset-utils.js';
import { setAssetsCache } from './assets-state.js';
import * as assetStore from './asset-store.js';
import { renderFilterPanelHTML, attachFilterListeners, applyFilters, getFilterState } from './assets-filter.js';
import { createTableSorter } from '../../utils/table-sort.js';
import { resolveFieldLabels, customTextFields, fixedTextFields } from './field-labels.js';
import { loadAssetFields, visibleFields, effectiveFieldLabel, optionLabel } from './asset-fields.js';
import { escapeHtml } from '../../utils/escape.js';
import { tName } from '../../modules/demo-mode.js';
import { scopeTenantParam, getMemberships } from '../../modules/tenant-scope.js';
import { getTemplate, applyOrder, SURFACES } from '../display/template-store.js';
import { locate } from '../pois/poi-locate.js';
import { poiState } from '../pois/poi-state.js';
import { ensurePoiData } from '../pois/poi-data.js';
import { featureEnabled } from '../../modules/features.js';

// Resolve a coordinate to a POI/geofence name — the SAME lookup the trips view
// and the map use (poi-locate.locate), fed from the shared, localStorage-cached
// poiState (no per-row fetch). Memoised by coordinate so re-sorting/re-filtering
// the (large) list never recomputes the point-in-fence math.
const _placeMemo = new Map(); // "lat,lon" → hit | null
poiState.onChange(() => _placeMemo.clear()); // POI bundle refreshed → drop cache

function resolvePlace(lat, lon) {
  if (!lat || !lon) return null;
  const key = `${lat},${lon}`;
  if (_placeMemo.has(key)) return _placeMemo.get(key);
  const hit = poiState.pois?.length ? locate(lat, lon, poiState.pois) : null;
  _placeMemo.set(key, hit);
  return hit;
}

// ── per-column cell renderers (return full <td>) ──────────────────────────────
function tdLastPosition(a) {
  const lat = a.lastLat || '', lon = a.lastLon || '';
  let inner;
  if (!lat || !lon) {
    inner = '<span class="text-muted">—</span>';
  } else {
    // data-lat/data-lon stay on the <td> regardless of the label, so the
    // hover mini-map + exact coordinates keep working.
    const url = `https://www.google.com/maps?q=${lat},${lon}`;
    const place = resolvePlace(lat, lon);
    if (place && place.exact) {
      // Inside a geofence → show the place (short name preferred, else full).
      const label = place.shortName || place.name;
      inner = `<a href="${url}" target="_blank" rel="noopener noreferrer" title="Inside this geofence — ${escapeHtml(place.name)}"
                  class="text-info text-decoration-none d-flex align-items-center gap-1">
                 <i class="fa-solid fa-location-dot"></i><small>${escapeHtml(label)}</small></a>`;
    } else if (place) {
      // No geofence covers it → name the nearest POI but SHOW the distance so an
      // approximation never reads as a confirmed location (as in the trips view).
      const away = place.distanceM >= 1000 ? `${(place.distanceM / 1000).toFixed(1)} km` : `${place.distanceM} m`;
      const label = place.shortName || place.name;
      inner = `<a href="${url}" target="_blank" rel="noopener noreferrer" title="No geofence covers this point — nearest POI is ${away} away"
                  class="text-body-secondary text-decoration-none d-flex align-items-center gap-1">
                 <i class="fa-solid fa-location-dot"></i><small>near ${escapeHtml(label)} <span class="small">(~${away})</span></small></a>`;
    } else {
      // No POI match → raw coordinates are useless to a person, so show just a
      // map-pin link (click to open the exact point) with no coordinate text.
      inner = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-info text-decoration-none" data-bs-toggle="tooltip" title="Open location in maps">
                 <i class="fa-solid fa-map-marker-alt"></i></a>`;
    }
  }
  return `<td class="last-position-cell" data-asset-id="${a.assetUUID}" data-lat="${lat}" data-lon="${lon}">${inner}</td>`;
}
function tdDevices(a) {
  const devs = a.assignedDevices || [];
  return `<td class="text-center">${devs.length
    ? devs.map(d => `<i class="fa-solid fa-microchip text-secondary mx-1" style="cursor:default;" data-bs-toggle="tooltip" title="${escapeHtml(d.deviceId)}${d.serialNumber ? ' · SN ' + escapeHtml(d.serialNumber) : ''}"></i>`).join('')
    : '<span class="text-muted">—</span>'}</td>`;
}

// Front-photo thumbnails, fetched ONCE per tenant as a batch of presigned URLs
// (docs/asset-files.md §8). assetUUID → url. Empty until the batch resolves;
// the column then repaints, so the list never waits on images.
let _thumbs = {};
export function assetThumbUrl(assetUUID) { return _thumbs[assetUUID] || null; }

async function loadThumbs(tenantParam) {
  // Without the flag the endpoint is not routed at all, so asking would mean a
  // 404 on every single list render.
  if (!featureEnabled('assetFiles')) return {};
  if (!tenantParam || tenantParam === 'unassigned') return {};
  try {
    const res = await window.authenticatedFetch(`api/v1/files/thumbs?tenant=${encodeURIComponent(tenantParam)}`);
    if (!res.ok) return {};
    return (await res.json()).thumbs || {};
  } catch { return {}; }
}

// Round front-image thumbnail, immediately after the status dot. Assets without
// a photo get a subtle type icon so the column never looks broken.
function tdPhoto(a) {
  const url = _thumbs[a.assetUUID];
  const inner = url
    ? `<img src="${escapeHtml(url)}" alt="" loading="lazy"
            style="width:34px;height:34px;border-radius:50%;object-fit:cover;background:#f1f5f9;">`
    : `<span class="d-inline-flex align-items-center justify-content-center text-secondary"
             style="width:34px;height:34px;border-radius:50%;background:#f1f5f9;font-size:13px;">
         <i class="fa-solid fa-plane-up"></i></span>`;
  return `<td style="width:44px;">${inner}</td>`;
}

// Leading status indicator: a small green (active) / red (inactive) dot, so the
// status no longer needs its own column.
function tdStatusDot(a) {
  const active = (a.status || 'active') === 'active';
  return `<td class="text-center" style="width:26px;"><span class="d-inline-block rounded-circle align-middle"
    style="width:11px;height:11px;background:${active ? '#198754' : '#dc3545'};" data-bs-toggle="tooltip"
    title="${active ? 'Active' : 'Inactive'}"></span></td>`;
}

// Build the column descriptor for the current label tenant + registry fields.
// Registry select fields (Asset type, Engine type, …) appear after Serial and
// vanish entirely for a tenant that has them switched off.
function buildColumns(labelTenant, regDefs, showTenant = true) {
  const L = resolveFieldLabels(labelTenant);
  // Engine Serial Number (ESN) is the leading, primary identifier column when the
  // tenant has it switched on; otherwise the reference keeps the primary styling.
  const esn = fixedTextFields(labelTenant).find(f => f.key === 'engine_serialNo' && f.enabled);
  const primaryCell = (v) => `<td><strong class="text-primary">${escapeHtml(v || '—')}</strong></td>`;

  const cols = [
    { key: 'statusdot', header: '', sortable: false, cell: a => tdStatusDot(a) },
    // Front photo, right after the status dot (docs/asset-files.md §8).
    { key: 'photo', header: '', sortable: false, cell: a => tdPhoto(a) },
  ];
  if (esn) cols.push({ key: 'engine_serialNo', header: esn.label, type: 'string', sortable: true,
    accessor: a => a.engine_serialNo || '', cell: a => primaryCell(a.engine_serialNo) });
  cols.push({ key: 'reference', header: L.reference, type: 'string', sortable: true,
    accessor: a => a.reference || a.assetId || '',
    cell: a => esn ? `<td>${escapeHtml(a.reference || a.assetId || '—')}</td>` : primaryCell(a.reference || a.assetId) });
  cols.push(
    { key: 'label', header: L.label, type: 'string', sortable: true,
      accessor: a => a.label || '', cell: a => `<td>${escapeHtml(a.label || '—')}</td>` },
    { key: 'serial', header: L.serial, type: 'string', sortable: true,
      accessor: a => a.serial || '', cell: a => `<td>${escapeHtml(a.serial || '—')}</td>` },
  );
  for (const f of visibleFields(regDefs, labelTenant)) {
    cols.push({
      key: f.fieldId, header: effectiveFieldLabel(f, labelTenant), type: 'string', sortable: true,
      accessor: a => optionLabel(f, a[f.fieldId]) || '',
      cell: a => {
        const v = optionLabel(f, a[f.fieldId]);
        return `<td>${v ? `<span class="badge bg-secondary">${escapeHtml(v)}</span>` : '<span class="text-muted">—</span>'}</td>`;
      },
    });
  }
  // Optional free-text columns — tenant custom fields + fixed standard fields, when
  // enabled. ESN is excluded here because it is already the leading primary column.
  for (const cf of [...customTextFields(labelTenant), ...fixedTextFields(labelTenant)].filter(c => c.enabled && c.key !== 'engine_serialNo')) {
    cols.push({
      key: cf.key, header: cf.label, type: 'string', sortable: true,
      accessor: a => a[cf.key] || '',
      cell: a => `<td>${a[cf.key] ? escapeHtml(a[cf.key]) : '<span class="text-muted">—</span>'}</td>`,
    });
  }
  // Tenant column only when the (filtered) rows span more than one tenant.
  if (showTenant) cols.push(
    { key: 'tenant', header: 'Tenant', type: 'string', sortable: true,
      accessor: a => a.tenantName || '', cell: a => `<td><span class="badge bg-info">${tName(a.tenantName)}</span></td>` });
  cols.push(
    { key: 'lastPosition', header: 'Last Position', sortable: false, cell: tdLastPosition },
    { key: 'lastUpdated', header: 'Last Updated', type: 'date', sortable: true,
      accessor: a => a.lastUpdated || '',
      cell: a => `<td><small class="text-muted">${a.lastUpdated ? new Date(a.lastUpdated).toLocaleString(navigator.language) : '-'}</small></td>` },
    { key: 'devices', header: 'Paired Devices', type: 'number', sortable: true,
      accessor: a => (a.assignedDevices?.length || 0), cell: tdDevices },
    { key: 'actions', header: '', sortable: false,
      cell: a => `<td><button class="btn btn-sm btn-link text-dark action-menu" data-asset-id="${a.assetUUID}" style="cursor:default;">⋮</button></td>` },
  );
  // Apply the tenant's / user's column template: which columns show, in which
  // order (docs/asset-display-templates.md). Without a template the built-in
  // order below is returned unchanged. 'actions' is never hidden — it carries
  // the row menu — and unknown template keys are ignored by applyOrder.
  try {
    const tpl = getTemplate(SURFACES.listColumns);
    if (tpl?.columns?.length) {
      const byKey = new Map(cols.map(c => [c.key, c]));
      const ordered = applyOrder(cols.map((c, i) => ({ key: c.key, defaultOrder: (i + 1) * 10 })), tpl)
        .map(o => byKey.get(o.key))
        .filter(Boolean);
      const actions = cols.find(c => c.key === 'actions');
      if (actions && !ordered.includes(actions)) ordered.push(actions);
      if (ordered.length) return ordered;
    }
  } catch (e) { /* template store not ready → built-in order */ }
  return cols;
}

export async function initAssetsListPage(container, providedAssets = null) {
  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="h4 mb-0">Assets <span class="badge bg-primary ms-2" id="assets-count-badge">0</span></h2>
      <button id="btn-new-asset-page" class="btn btn-success">
        <i class="fa-solid fa-plus me-2"></i> Create New Asset
      </button>
    </div>

    <div id="assets-filter-slot"></div>

    <div class="card shadow-sm">
      <div class="card-body p-0 table-responsive">
        <table class="table table-hover mb-0" id="assets-table">
          <thead class="table-light"><tr></tr></thead>
          <tbody></tbody>
        </table>
      </div>
    </div>
  `;

  const tbody = container.querySelector('tbody');
  const theadRow = container.querySelector('#assets-table thead tr');
  let allAssets = [];
  let tenants = [];
  let regDefs = [];

  // Dynamic columns: the sorter + header are (re)built whenever the visible
  // column set changes (e.g. a tenant filter that hides a registry field).
  let columns = [];
  let colSig = null;
  let sorter = null;

  function syncColumns(showTenant = true) {
    // White-label field labels resolve from the tenant in view: the per-page
    // filter first, else the GLOBAL tenant scope, else a single membership.
    const scopeTid = scopeTenantParam();
    const selTid = getFilterState().tenantId || (scopeTid && scopeTid !== 'unassigned' ? scopeTid : null);
    const labelTenant = selTid ? tenants.find(t => t.tenantId === selTid)
      : (tenants.length === 1 ? tenants[0] : null);
    const next = buildColumns(labelTenant, regDefs, showTenant);
    const sig = next.map(c => c.key).join('|');
    columns = next;
    if (sig !== colSig) {
      colSig = sig;
      theadRow.innerHTML = columns.map(c =>
        `<th${c.key === 'actions' ? ' style="width:50px;"' : ''}>${escapeHtml(c.header)}</th>`).join('');
      sorter = createTableSorter(columns.map(c => ({ type: c.type, sortable: c.sortable, accessor: c.accessor })), () => renderTable());
      sorter.decorate(theadRow);
    } else {
      // Same structure → just refresh header labels (tenant rename).
      const ths = theadRow.querySelectorAll('th');
      columns.forEach((c, i) => { if (ths[i] && ths[i].firstChild) ths[i].firstChild.nodeValue = c.header; });
    }
  }

  // Create New Asset Button
  document.getElementById('btn-new-asset-page')?.addEventListener('click', () => {
    const modalEl = document.getElementById('newAssetModal');
    if (modalEl) (bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl)).show();
  });

  // Filter-Panel + tenants + field registry. Non-admins lack listTenants — the
  // scope memberships (own tenants, names included) fill the filter instead.
  try {
    [tenants, regDefs] = await Promise.all([
      loadTenants().catch(() => getMemberships()),
      loadAssetFields().catch(() => []),
    ]);
  }
  catch (e) { /* non-fatal */ }
  document.getElementById('assets-filter-slot').innerHTML = renderFilterPanelHTML(tenants, regDefs);
  attachFilterListeners(() => renderTable());

  // Refresh hook for new-asset-modal.js
  window.refreshAssets = async () => { await loadAssetsTable(true); };

  // ── Render table (filtered) ───────────────────────────────────────────────
  function renderTable() {
    const filteredRaw = applyFilters(allAssets);
    // Hide the tenant column when everything on screen belongs to one tenant.
    const showTenant = new Set(filteredRaw.map(a => a.tenantId)).size > 1;
    syncColumns(showTenant);
    const filtered = sorter.sort(filteredRaw);
    const total    = allAssets.length;

    const badge = document.getElementById('assets-count-badge');
    if (badge) badge.textContent = filtered.length < total ? `${filtered.length} / ${total}` : total;

    if (filtered.length === 0) {
      tbody.innerHTML = `<tr><td colspan="${columns.length}" class="text-center text-muted py-4">
        ${total === 0 ? 'No assets found.' : 'No assets match the current filters.'}
      </td></tr>`;
      return;
    }

    // Dispose any live tooltips before replacing the rows (avoid orphaned popups).
    tbody.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => { try { bootstrap.Tooltip.getInstance(el)?.dispose(); } catch {} });
    // Same for the last-position map popovers, which live in <body> and would
    // otherwise be stranded on screen when the rows are replaced.
    disposeLastPositionHovers();

    tbody.innerHTML = filtered.map(asset =>
      `<tr data-asset-id="${asset.assetUUID}" class="asset-row" style="cursor:pointer;">${
        columns.map(c => c.cell(asset)).join('')
      }</tr>`).join('');

    setupLastPositionHover(tbody);
    // Bootstrap tooltips: status dot, paired-device icons, map-pin.
    tbody.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => { try { new bootstrap.Tooltip(el); } catch {} });

    tbody.querySelectorAll('.asset-row').forEach(row => {
      row.addEventListener('click', e => {
        if (e.target.closest('.action-menu') || e.target.closest('a')) return;
        history.pushState(null, '', `#assets/${row.dataset.assetId}`);
        window.showAssetDetail(row.dataset.assetId);
      });
    });

    tbody.querySelectorAll('.action-menu').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const asset = allAssets.find(a => a.assetUUID === btn.dataset.assetId);
        if (asset) showActionMenu(e, asset, () => loadAssetsTable(true));
      });
    });
  }

  // ── Load data ─────────────────────────────────────────────────────────────
  // Delta cache: render instantly from the persisted cache (no spinner), then
  // sync only the changes and re-render (docs/performance.md). forceRefresh
  // (after a create/edit/delete) requests a full reload.
  async function loadAssetsTable(forceRefresh = false) {
    if (providedAssets && !forceRefresh) {
      allAssets = providedAssets;
      renderTable();
      return;
    }
    try {
      await assetStore.init();
      const cached = assetStore.getCached();
      if (cached.length && !forceRefresh) {
        allAssets = cached; setAssetsCache(allAssets); renderTable();  // instant paint
      } else {
        window.showPlaneSpinner(null, 'Loading assets...');
      }
      const { assets } = await assetStore.sync({ force: forceRefresh });
      allAssets = assets; setAssetsCache(allAssets); renderTable();
    } catch (err) {
      console.error(err);
      window.showToast('Failed to load assets. Please try again.', 'danger', 'Error');
      if (!allAssets.length) {
        tbody.innerHTML = `<tr><td colspan="${columns.length || 8}" class="text-center text-danger py-4">Error loading assets</td></tr>`;
      }
    } finally {
      window.hidePlaneSpinner();
    }
  }

  // Load the (localStorage-cached) POI bundle in parallel with the assets; when
  // it lands, drop the memo and repaint so place names replace coordinates.
  // Non-fatal: a POI failure just leaves coordinates showing.
  ensurePoiData().then(() => { _placeMemo.clear(); if (allAssets.length) renderTable(); }).catch(() => {});

  // Front-photo thumbnails: one batched call, repaint when it lands. Never
  // blocks the table — assets without a photo simply keep the type icon.
  loadThumbs(scopeTenantParam()).then(t => {
    if (!Object.keys(t).length) return;
    _thumbs = t;
    if (allAssets.length) renderTable();
  });

  setupBatchImport();
  await loadAssetsTable();
}
